import React, { useState, useEffect, useRef } from 'react';
import { 
  Image, FileText, Video, Folder, Plus, Search, Filter, Download,
  MoreVertical, Trash2, Edit3, X, Loader2, Grid, List, Eye,
  Star, Tag, Clock, User, ChevronRight, Upload, File, Check
} from 'lucide-react';
import { useUserEmail } from '../../context/UserAuthContext';

const API = typeof window !== 'undefined' ? window.location.origin : '';

const ASSET_TYPES = {
  logo: { icon: Star, color: 'bg-yellow-500/20 text-yellow-400', label: 'Logo' },
  photo: { icon: Image, color: 'bg-blue-500/20 text-blue-400', label: 'Photo' },
  video: { icon: Video, color: 'bg-purple-500/20 text-purple-400', label: 'Video' },
  document: { icon: FileText, color: 'bg-green-500/20 text-green-400', label: 'Document' },
  template: { icon: Grid, color: 'bg-pink-500/20 text-pink-400', label: 'Template' },
  guideline: { icon: File, color: 'bg-orange-500/20 text-orange-400', label: 'Guideline' }
};

const FOLDERS = [
  { id: 'all', label: 'All Assets', icon: Folder },
  { id: 'logos', label: 'Logos', icon: Star },
  { id: 'photos', label: 'Photos', icon: Image },
  { id: 'videos', label: 'Videos', icon: Video },
  { id: 'documents', label: 'Documents', icon: FileText },
  { id: 'templates', label: 'Templates', icon: Grid },
  { id: 'guidelines', label: 'Guidelines', icon: File }
];

const AEAssets = () => {
  const userEmail = useUserEmail();
  const [loading, setLoading] = useState(true);
  const [assets, setAssets] = useState([]);
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState('all');
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Upload state
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadForm, setUploadForm] = useState({
    name: '',
    asset_type: 'photo',
    folder: 'general',
    description: '',
    tags: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkUploadResults, setBulkUploadResults] = useState(null);
  
  const fileInputRef = useRef(null);
  const bulkFileInputRef = useRef(null);

  useEffect(() => {
    if (userEmail) {
      checkRole();
      fetchAssets();
      fetchFolders();
    }
  }, [userEmail, selectedFolder]);

  const checkRole = async () => {
    try {
      const res = await fetch(`${API}/api/always-elevate/user-role?user_email=${encodeURIComponent(userEmail)}`);
      const data = await res.json();
      setIsAdmin(data.role === 'admin');
    } catch (err) {
      console.error('Error checking role:', err);
    }
  };

  const fetchAssets = async () => {
    try {
      let url = `${API}/api/always-elevate/assets?user_email=${encodeURIComponent(userEmail)}`;
      if (selectedFolder && selectedFolder !== 'all') {
        url += `&folder=${selectedFolder}`;
      }
      const res = await fetch(url);
      const data = await res.json();
      if (data.success) {
        setAssets(data.data);
      }
    } catch (err) {
      console.error('Error fetching assets:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchFolders = async () => {
    try {
      const res = await fetch(`${API}/api/always-elevate/assets/folders?user_email=${encodeURIComponent(userEmail)}`);
      const data = await res.json();
      if (data.success) {
        setFolders(data.data);
      }
    } catch (err) {
      console.error('Error fetching folders:', err);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      // Auto-fill name from filename
      if (!uploadForm.name) {
        const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ');
        setUploadForm(prev => ({ ...prev, name: nameWithoutExt }));
      }
      // Auto-detect asset type
      const ext = file.name.split('.').pop().toLowerCase();
      if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) {
        setUploadForm(prev => ({ ...prev, asset_type: 'photo' }));
      } else if (['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext)) {
        setUploadForm(prev => ({ ...prev, asset_type: 'document' }));
      } else if (['mp4', 'mov'].includes(ext)) {
        setUploadForm(prev => ({ ...prev, asset_type: 'video' }));
      } else if (['psd', 'ai'].includes(ext)) {
        setUploadForm(prev => ({ ...prev, asset_type: 'template' }));
      }
    }
  };

  const handleBulkFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setBulkFiles(files);
  };

  const handleUpload = async () => {
    if (!selectedFile || !uploadForm.name) return;
    
    setUploading(true);
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('name', uploadForm.name);
      formData.append('asset_type', uploadForm.asset_type);
      formData.append('folder', uploadForm.folder);
      formData.append('description', uploadForm.description);
      formData.append('tags', uploadForm.tags);
      
      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          setUploadProgress(Math.round((e.loaded / e.total) * 100));
        }
      });
      
      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
          setUploading(false);
          if (xhr.status === 200) {
            const response = JSON.parse(xhr.responseText);
            if (response.success) {
              setShowUploadModal(false);
              setSelectedFile(null);
              setUploadForm({
                name: '',
                asset_type: 'photo',
                folder: 'general',
                description: '',
                tags: ''
              });
              fetchAssets();
              fetchFolders();
            }
          } else {
            console.error('Upload failed:', xhr.responseText);
            alert('Upload failed. Please try again.');
          }
        }
      };
      
      xhr.open('POST', `${API}/api/always-elevate/assets/upload?user_email=${encodeURIComponent(userEmail)}`);
      xhr.send(formData);
      
    } catch (err) {
      console.error('Error uploading:', err);
      setUploading(false);
      alert('Upload failed. Please try again.');
    }
  };

  const handleBulkUpload = async () => {
    if (bulkFiles.length === 0) return;
    
    setUploading(true);
    setUploadProgress(0);
    setBulkUploadResults(null);
    
    try {
      const formData = new FormData();
      bulkFiles.forEach(file => {
        formData.append('files', file);
      });
      formData.append('folder', uploadForm.folder);
      
      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          setUploadProgress(Math.round((e.loaded / e.total) * 100));
        }
      });
      
      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
          setUploading(false);
          if (xhr.status === 200) {
            const response = JSON.parse(xhr.responseText);
            setBulkUploadResults(response);
            if (response.success && response.total_uploaded > 0) {
              fetchAssets();
              fetchFolders();
            }
          } else {
            console.error('Bulk upload failed:', xhr.responseText);
            alert('Bulk upload failed. Please try again.');
          }
        }
      };
      
      xhr.open('POST', `${API}/api/always-elevate/assets/upload-bulk?user_email=${encodeURIComponent(userEmail)}`);
      xhr.send(formData);
      
    } catch (err) {
      console.error('Error bulk uploading:', err);
      setUploading(false);
      alert('Bulk upload failed. Please try again.');
    }
  };

  const handleDeleteAsset = async (assetId) => {
    if (!window.confirm('Are you sure you want to delete this asset?')) return;
    
    try {
      const res = await fetch(`${API}/api/always-elevate/assets/${assetId}?user_email=${encodeURIComponent(userEmail)}`, {
        method: 'DELETE'
      });
      const data = await res.json();
      if (data.success) {
        fetchAssets();
        setSelectedAsset(null);
      }
    } catch (err) {
      console.error('Error deleting asset:', err);
    }
  };

  const filteredAssets = assets.filter(asset => {
    if (searchQuery && !asset.name.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    return true;
  });

  const getFolderCount = (folderId) => {
    if (folderId === 'all') return assets.length;
    const folder = folders.find(f => f._id === folderId);
    return folder?.count || 0;
  };

  const resetUploadModal = () => {
    setShowUploadModal(false);
    setSelectedFile(null);
    setUploadForm({
      name: '',
      asset_type: 'photo',
      folder: 'general',
      description: '',
      tags: ''
    });
  };

  const resetBulkUploadModal = () => {
    setShowBulkUploadModal(false);
    setBulkFiles([]);
    setBulkUploadResults(null);
    setUploadForm(prev => ({ ...prev, folder: 'general' }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#C4A052]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="ae-assets-page">
      {/* Header */}
      <header className="border-b border-white/10 px-6 py-6">
        <div className="max-w-[1800px] mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#C4A052] text-sm font-medium tracking-wider">ALWAYS ELEVATE</p>
              <h1 className="text-3xl font-bold mt-1" style={{ fontFamily: 'Anton, sans-serif' }} data-testid="ae-assets-title">
                Brand Assets
              </h1>
              <p className="text-gray-400 text-sm mt-1">{assets.length} assets in library</p>
            </div>
            <div className="flex items-center gap-3">
              {/* View Toggle */}
              <div className="flex items-center bg-white/5 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded transition-colors ${viewMode === 'grid' ? 'bg-[#C4A052] text-black' : 'text-gray-400'}`}
                  data-testid="view-grid-btn"
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded transition-colors ${viewMode === 'list' ? 'bg-[#C4A052] text-black' : 'text-gray-400'}`}
                  data-testid="view-list-btn"
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
              
              {/* Bulk Upload Button */}
              <button
                onClick={() => setShowBulkUploadModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white rounded-lg font-medium hover:bg-white/20 transition-colors"
                data-testid="bulk-upload-btn"
              >
                <Upload className="w-4 h-4" />
                Bulk Upload
              </button>
              
              {/* Single Upload Button */}
              <button
                onClick={() => setShowUploadModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-[#C4A052] text-black rounded-lg font-medium hover:bg-[#D4B062] transition-colors"
                data-testid="upload-asset-btn"
              >
                <Plus className="w-4 h-4" />
                Upload Asset
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-[1800px] mx-auto px-6 py-8">
        <div className="flex gap-6">
          {/* Sidebar - Folders */}
          <div className="w-56 flex-shrink-0">
            <div className="bg-black border border-white/20 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-white/10">
                <h3 className="font-semibold text-sm">Folders</h3>
              </div>
              <div className="p-2">
                {FOLDERS.map(folder => {
                  const FolderIcon = folder.icon;
                  const count = getFolderCount(folder.id);
                  return (
                    <button
                      key={folder.id}
                      onClick={() => setSelectedFolder(folder.id)}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                        selectedFolder === folder.id 
                          ? 'bg-[#C4A052]/20 text-[#C4A052]' 
                          : 'text-gray-400 hover:bg-white/5'
                      }`}
                      data-testid={`folder-${folder.id}`}
                    >
                      <div className="flex items-center gap-2">
                        <FolderIcon className="w-4 h-4" />
                        <span>{folder.label}</span>
                      </div>
                      <span className="text-xs opacity-60">{count}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {/* Search */}
            <div className="relative mb-6">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input
                type="text"
                placeholder="Search assets..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-black border border-white/20 rounded-lg pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-[#C4A052]"
                data-testid="asset-search"
              />
            </div>

            {/* Assets Grid/List */}
            {filteredAssets.length === 0 ? (
              <div className="bg-black border border-white/20 rounded-xl p-12 text-center">
                <Image className="w-16 h-16 mx-auto mb-4 text-gray-600" />
                <h3 className="text-lg font-medium text-gray-400 mb-2">No Assets Found</h3>
                <p className="text-gray-500 text-sm mb-4">
                  {selectedFolder === 'all' 
                    ? 'Upload your first brand asset to get started'
                    : `No assets in this folder yet`}
                </p>
                <button
                  onClick={() => setShowUploadModal(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#C4A052] text-black rounded-lg font-medium hover:bg-[#D4B062] transition-colors"
                >
                  <Upload className="w-4 h-4" />
                  Upload Asset
                </button>
              </div>
            ) : viewMode === 'grid' ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="assets-grid">
                {filteredAssets.map(asset => {
                  const assetType = ASSET_TYPES[asset.asset_type] || ASSET_TYPES.document;
                  const AssetIcon = assetType.icon;
                  const isImage = ['photo', 'logo'].includes(asset.asset_type) && asset.file_url;
                  
                  return (
                    <div
                      key={asset.id}
                      onClick={() => setSelectedAsset(asset)}
                      className={`bg-white/5 border rounded-xl overflow-hidden cursor-pointer ${
                        selectedAsset?.id === asset.id ? 'border-[#C4A052]' : 'border-white/10'
                      }`}
                      data-testid={`asset-card-${asset.id}`}
                    >
                      {/* Thumbnail */}
                      <div className="aspect-square bg-white/5 flex items-center justify-center relative overflow-hidden">
                        {isImage ? (
                          <img 
                            src={`${API}${asset.file_url}`} 
                            alt={asset.name}
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <AssetIcon className="w-12 h-12 text-gray-500" />
                        )}
                        <div className={`absolute top-2 right-2 p-1.5 rounded-lg ${assetType.color}`}>
                          <AssetIcon className="w-3 h-3" />
                        </div>
                      </div>
                      {/* Info */}
                      <div className="p-3">
                        <h4 className="font-medium text-sm truncate">{asset.name}</h4>
                        <p className="text-xs text-gray-500 mt-1">{assetType.label}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="bg-black border border-white/20 rounded-xl overflow-hidden" data-testid="assets-list">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 uppercase">Name</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 uppercase">Type</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 uppercase">Folder</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 uppercase">Added</th>
                      <th className="text-right px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {filteredAssets.map(asset => {
                      const assetType = ASSET_TYPES[asset.asset_type] || ASSET_TYPES.document;
                      const AssetIcon = assetType.icon;
                      
                      return (
                        <tr
                          key={asset.id}
                          onClick={() => setSelectedAsset(asset)}
                          className={`cursor-pointer transition-colors hover:bg-white/5 ${
                            selectedAsset?.id === asset.id ? 'bg-[#C4A052]/10' : ''
                          }`}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className={`p-2 rounded-lg ${assetType.color}`}>
                                <AssetIcon className="w-4 h-4" />
                              </div>
                              <span className="font-medium text-sm">{asset.name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-400">{assetType.label}</td>
                          <td className="px-4 py-3 text-sm text-gray-400 capitalize">{asset.folder}</td>
                          <td className="px-4 py-3 text-sm text-gray-400">
                            {new Date(asset.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <ChevronRight className="w-4 h-4 text-gray-500 inline" />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Asset Detail Modal */}
      {selectedAsset && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0f0f0f] border border-white/10 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <h3 className="text-lg font-bold">{selectedAsset.name}</h3>
              <button 
                onClick={() => setSelectedAsset(null)} 
                className="p-2 hover:bg-white/10 rounded-lg"
                data-testid="close-asset-modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              {/* Preview */}
              <div className="aspect-video bg-white/5 rounded-xl flex items-center justify-center mb-6 overflow-hidden">
                {selectedAsset.file_url && ['photo', 'logo'].includes(selectedAsset.asset_type) ? (
                  <img 
                    src={`${API}${selectedAsset.file_url}`} 
                    alt={selectedAsset.name}
                    className="max-w-full max-h-full object-contain"
                  />
                ) : (
                  (() => {
                    const assetType = ASSET_TYPES[selectedAsset.asset_type] || ASSET_TYPES.document;
                    const AssetIcon = assetType.icon;
                    return <AssetIcon className="w-20 h-20 text-gray-500" />;
                  })()
                )}
              </div>

              {/* Metadata */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Type</p>
                  <p className="text-sm capitalize">{selectedAsset.asset_type}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Folder</p>
                  <p className="text-sm capitalize">{selectedAsset.folder}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Added</p>
                  <p className="text-sm">{new Date(selectedAsset.created_at).toLocaleDateString()}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Added By</p>
                  <p className="text-sm">{selectedAsset.created_by?.split('@')[0] || 'Unknown'}</p>
                </div>
              </div>

              {/* Description */}
              {selectedAsset.description && (
                <div className="mb-6">
                  <p className="text-xs text-gray-500 mb-1">Description</p>
                  <p className="text-sm text-gray-300">{selectedAsset.description}</p>
                </div>
              )}

              {/* Tags */}
              {selectedAsset.tags && selectedAsset.tags.length > 0 && (
                <div className="mb-6">
                  <p className="text-xs text-gray-500 mb-2">Tags</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedAsset.tags.map((tag, idx) => (
                      <span key={idx} className="px-2 py-1 bg-white/10 rounded text-xs">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* File Info */}
              {selectedAsset.metadata && (
                <div className="mb-6">
                  <p className="text-xs text-gray-500 mb-2">File Information</p>
                  <div className="bg-white/5 rounded-lg p-3 text-xs text-gray-400">
                    {Object.entries(selectedAsset.metadata).map(([key, value]) => (
                      <div key={key} className="flex justify-between py-1">
                        <span className="capitalize">{key.replace(/_/g, ' ')}</span>
                        <span>{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <div className="p-6 border-t border-white/10 flex justify-between gap-3">
              <div>
                {isAdmin && (
                  <button
                    onClick={() => handleDeleteAsset(selectedAsset.id)}
                    className="flex items-center gap-2 px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                    data-testid="delete-asset-btn"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </button>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setSelectedAsset(null)}
                  className="px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
                >
                  Close
                </button>
                {selectedAsset.file_url && (
                  <a
                    href={`${API}${selectedAsset.file_url}`}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 bg-[#C4A052] text-black rounded-lg font-medium hover:bg-[#D4B062] transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Single Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0f0f0f] border border-white/10 rounded-xl max-w-lg w-full">
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <h3 className="text-lg font-bold">Upload Asset</h3>
              <button onClick={resetUploadModal} className="p-2 hover:bg-white/10 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* File Drop Zone */}
              <div 
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                  selectedFile ? 'border-[#C4A052] bg-[#C4A052]/10' : 'border-white/20 hover:border-[#C4A052]/50 hover:scale-[1.02]'
                }`}
              >
                {selectedFile ? (
                  <>
                    <Check className="w-12 h-12 mx-auto mb-3 text-[#C4A052]" />
                    <p className="text-[#C4A052] font-medium">{selectedFile.name}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {(selectedFile.size / 1024).toFixed(1)} KB
                    </p>
                  </>
                ) : (
                  <>
                    <Upload className="w-12 h-12 mx-auto mb-3 text-gray-500" />
                    <p className="text-gray-400 mb-1">Click to select a file</p>
                    <p className="text-xs text-gray-500">PNG, JPG, PDF, PSD, MP4, etc. (max 50MB)</p>
                  </>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={handleFileSelect}
                  accept=".png,.jpg,.jpeg,.gif,.webp,.pdf,.psd,.ai,.mp4,.mov,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                />
              </div>

              {/* Name */}
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Name *</label>
                <input
                  type="text"
                  value={uploadForm.name}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C4A052]"
                  placeholder="Asset name"
                />
              </div>

              {/* Type & Folder */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Asset Type</label>
                  <select
                    value={uploadForm.asset_type}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, asset_type: e.target.value }))}
                    className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C4A052]"
                  >
                    {Object.entries(ASSET_TYPES).map(([key, val]) => (
                      <option key={key} value={key}>{val.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Folder</label>
                  <select
                    value={uploadForm.folder}
                    onChange={(e) => setUploadForm(prev => ({ ...prev, folder: e.target.value }))}
                    className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C4A052]"
                  >
                    {FOLDERS.filter(f => f.id !== 'all').map(folder => (
                      <option key={folder.id} value={folder.id}>{folder.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Description</label>
                <textarea
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C4A052] resize-none h-20"
                  placeholder="Optional description"
                />
              </div>

              {/* Tags */}
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Tags (comma separated)</label>
                <input
                  type="text"
                  value={uploadForm.tags}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, tags: e.target.value }))}
                  className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C4A052]"
                  placeholder="e.g. headshot, professional, media-kit"
                />
              </div>

              {/* Progress */}
              {uploading && (
                <div className="bg-white/5 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-400">Uploading...</span>
                    <span className="text-sm text-[#C4A052]">{uploadProgress}%</span>
                  </div>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-[#C4A052] transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
            
            <div className="p-6 border-t border-white/10 flex justify-end gap-3">
              <button
                onClick={resetUploadModal}
                className="px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
                disabled={uploading}
              >
                Cancel
              </button>
              <button 
                onClick={handleUpload}
                disabled={!selectedFile || !uploadForm.name || uploading}
                className="flex items-center gap-2 px-4 py-2 bg-[#C4A052] text-black rounded-lg font-medium hover:bg-[#D4B062] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4" />
                )}
                Upload
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Upload Modal */}
      {showBulkUploadModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0f0f0f] border border-white/10 rounded-xl max-w-lg w-full">
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <h3 className="text-lg font-bold">Bulk Upload Assets</h3>
              <button onClick={resetBulkUploadModal} className="p-2 hover:bg-white/10 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* File Drop Zone */}
              <div 
                onClick={() => bulkFileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                  bulkFiles.length > 0 ? 'border-[#C4A052] bg-[#C4A052]/10' : 'border-white/20 hover:border-[#C4A052]/50 hover:scale-[1.02]'
                }`}
              >
                {bulkFiles.length > 0 ? (
                  <>
                    <Check className="w-12 h-12 mx-auto mb-3 text-[#C4A052]" />
                    <p className="text-[#C4A052] font-medium">{bulkFiles.length} files selected</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {bulkFiles.map(f => f.name).join(', ')}
                    </p>
                  </>
                ) : (
                  <>
                    <Upload className="w-12 h-12 mx-auto mb-3 text-gray-500" />
                    <p className="text-gray-400 mb-1">Click to select multiple files</p>
                    <p className="text-xs text-gray-500">PNG, JPG, PDF, PSD, MP4, etc.</p>
                  </>
                )}
                <input
                  ref={bulkFileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={handleBulkFileSelect}
                  accept=".png,.jpg,.jpeg,.gif,.webp,.pdf,.psd,.ai,.mp4,.mov,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                />
              </div>

              {/* Folder Selection */}
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Upload to Folder</label>
                <select
                  value={uploadForm.folder}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, folder: e.target.value }))}
                  className="w-full bg-black border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C4A052]"
                >
                  {FOLDERS.filter(f => f.id !== 'all').map(folder => (
                    <option key={folder.id} value={folder.id}>{folder.label}</option>
                  ))}
                </select>
              </div>

              {/* Progress */}
              {uploading && (
                <div className="bg-white/5 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-400">Uploading {bulkFiles.length} files...</span>
                    <span className="text-sm text-[#C4A052]">{uploadProgress}%</span>
                  </div>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-[#C4A052] transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Results */}
              {bulkUploadResults && (
                <div className="bg-white/5 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Check className="w-5 h-5 text-green-400" />
                    <span className="font-medium">{bulkUploadResults.total_uploaded} files uploaded</span>
                  </div>
                  {bulkUploadResults.total_errors > 0 && (
                    <div className="text-sm text-red-400">
                      {bulkUploadResults.total_errors} files failed
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className="p-6 border-t border-white/10 flex justify-end gap-3">
              <button
                onClick={resetBulkUploadModal}
                className="px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
                disabled={uploading}
              >
                {bulkUploadResults ? 'Done' : 'Cancel'}
              </button>
              {!bulkUploadResults && (
                <button 
                  onClick={handleBulkUpload}
                  disabled={bulkFiles.length === 0 || uploading}
                  className="flex items-center gap-2 px-4 py-2 bg-[#C4A052] text-black rounded-lg font-medium hover:bg-[#D4B062] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4" />
                  )}
                  Upload All
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AEAssets;
